import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { gql, useMutation } from '@apollo/client';
import { Loader2, UploadIcon, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

const GET_CERTIFICATE_DETAILS = gql`
   query GetCertificateDetails($id: ID!) {
      getCertificateById(id: $id) {
         id
         certificateNumber
         certificateStatus
         applicantSignature {
            data
            contentType
         }
         ptprCertificateData {
            ownerName
         }
      }
   }
`;

const SIGN_PTPR_CERTIFICATE = gql`
   mutation SignPTPRCertificateByApplicant($id: ID!, $applicantSignature: String!) {
      signPTPRCertificateByApplicant(id: $id, applicantSignature: $applicantSignature) {
         id
         applicantSignature {
            data
            contentType
         }
      }
   }
`;

const ApplicantSignatureModal = ({ isOpen, onClose, certificate, onSignComplete }) => {
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [applicantSignature, setApplicantSignature] = useState(null);
   const fileInputRef = useRef(null);
   const [signPTPRCertificate] = useMutation(SIGN_PTPR_CERTIFICATE);

   const handleSignatureUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
         const reader = new FileReader();
         reader.onload = (e) => {
            setApplicantSignature(e.target.result);
         };
         reader.readAsDataURL(file);
      }
   };

   const triggerFileInput = (e) => {
      e.preventDefault();
      e.stopPropagation();
      fileInputRef.current.click();
   };

   const handleRemoveSignature = () => {
      setApplicantSignature(null);
      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleSign = async () => {
      if (!applicantSignature) {
         toast.error('Please upload your signature first');
         return;
      }

      if (!certificate?.id) {
         toast.error('Certificate data not found');
         return;
      }

      try {
         setIsSubmitting(true);
         const result = await signPTPRCertificate({
            variables: {
               id: certificate.id,
               applicantSignature: applicantSignature
            },
            refetchQueries: [
               {
                  query: GET_CERTIFICATE_DETAILS,
                  variables: { id: certificate.id }
               }
            ]
         });

         console.log('Signature submission result:', result);
         toast.success('Certificate signed successfully');
         onSignComplete?.();
         onClose();
      } catch (error) {
         console.error('Error signing certificate:', error);
         toast.error('Failed to sign certificate: ' + error.message);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-md">
            <DialogHeader>
               <DialogTitle>Sign PTPR Certificate</DialogTitle>
               <DialogDescription>
                  Please upload your signature to complete the certificate
               </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
               <div className="text-center">
                  <div className="relative h-32 mb-4 border-2 border-dashed rounded-md flex items-center justify-center bg-accent/50">
                     {applicantSignature ? (
                        <>
                           <img
                              src={applicantSignature}
                              alt="Applicant Signature"
                              className="max-w-full max-h-full object-contain"
                           />
                           <Button
                              variant="ghost"
                              size="icon"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-100 hover:bg-red-200"
                              onClick={handleRemoveSignature}
                           >
                              <X className="h-4 w-4 text-red-600" />
                           </Button>
                        </>
                     ) : (
                        <p className="text-muted-foreground">Applicant Signature</p>
                     )}
                  </div>
                  <input
                     type="file"
                     ref={fileInputRef}
                     className="hidden"
                     accept="image/*"
                     onChange={handleSignatureUpload}
                  />
                  <Button
                     variant="outline"
                     size="sm"
                     className="mt-2"
                     onClick={triggerFileInput}
                  >
                     <UploadIcon className="h-4 w-4 mr-2" />
                     Upload Signature
                  </Button>
               </div>
            </div>

            <DialogFooter>
               <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
               </Button>
               <Button
                  onClick={handleSign}
                  disabled={isSubmitting || !applicantSignature}
                  className="bg-green-600 text-white hover:bg-green-700"
               >
                  {isSubmitting ? (
                     <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing...
                     </>
                  ) : (
                     'Sign Certificate'
                  )}
               </Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default ApplicantSignatureModal;